import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import api from '../services/api';
import autoPhoneModalService from '../services/autoPhoneModalService';
import {
  getLocalStorageItem,
  setLocalStorageItem,
  removeLocalStorageItem,
} from '../utils/safeStorage';

function shouldPromptForCaptainPhone(response) {
  if (!response || response.hasPhoneNumber) return false;
  return response.viewerIsCaptain === true;
}

function maybeShowPhoneModal(teamId, response, setShowPhoneModal) {
  if (!shouldPromptForCaptainPhone(response)) {
    autoPhoneModalService.cancelModalForTeam(teamId);
    return;
  }

  const lastSkipped = getLocalStorageItem(`phone-skipped-${teamId}`);
  const thirtyMinutesAgo = Date.now() - 30 * 60 * 1000;

  if (!lastSkipped || parseInt(lastSkipped, 10) < thirtyMinutesAgo) {
    setShowPhoneModal(true);
    autoPhoneModalService.scheduleModalForTeam(teamId);
  } else {
    const timeUntilExpiry = parseInt(lastSkipped, 10) + 30 * 60 * 1000 - Date.now();
    if (timeUntilExpiry > 0) {
      setTimeout(() => {
        autoPhoneModalService.scheduleModalForTeam(teamId);
      }, timeUntilExpiry);
    }
  }
}

// Hook: captain player profile phone is the team contact number for match chat revelation.
export const usePhoneNumberStatus = (teamId) => {
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [hasCheckedPhone, setHasCheckedPhone] = useState(false);
  const queryClient = useQueryClient();

  const { data: phoneStatus, isLoading, refetch } = useQuery(
    ['team-phone-status', teamId],
    async ({ signal }) => {
      const response = await api.get(`/teams/${teamId}/phone-status`, { signal });
      return response.data || response;
    },
    {
      enabled: !!teamId,
      retry: false,
      onSuccess: (response) => {
        maybeShowPhoneModal(teamId, response, setShowPhoneModal);
        setHasCheckedPhone(true);
      },
      onError: (error) => {
        const msg = String(error?.message || '');
        const aborted =
          error?.code === 'ERR_CANCELED' ||
          error?.code === 'ECONNABORTED' ||
          msg.toLowerCase().includes('abort');
        if (aborted) return;
        setHasCheckedPhone(true);
      },
    }
  );

  useEffect(() => {
    const handleAutoModal = (event) => {
      if (event.detail.teamId === teamId && shouldPromptForCaptainPhone(phoneStatus)) {
        setShowPhoneModal(true);
      }
    };

    window.addEventListener('showPhoneModal', handleAutoModal);
    return () => window.removeEventListener('showPhoneModal', handleAutoModal);
  }, [teamId, phoneStatus]);

  const handlePhoneModalClose = () => {
    setShowPhoneModal(false);
    setLocalStorageItem(`phone-skipped-${teamId}`, Date.now().toString());
  };

  const handlePhoneModalSuccess = (data) => {
    setShowPhoneModal(false);
    removeLocalStorageItem(`phone-skipped-${teamId}`);
    autoPhoneModalService.cancelModalForTeam(teamId);
    const saved =
      data?.phoneNumber || data?.phone_number || phoneStatus?.phoneNumber || '';
    queryClient.setQueryData(['team-phone-status', teamId], (prev) => ({
      ...(prev || {}),
      hasPhoneNumber: true,
      phoneNumber: saved,
      viewerIsCaptain: prev?.viewerIsCaptain ?? true,
    }));
    queryClient.invalidateQueries(['team-phone-status', teamId]);
    setHasCheckedPhone(true);
  };

  return {
    showPhoneModal,
    hasPhoneNumber: phoneStatus?.hasPhoneNumber || false,
    phoneNumber: phoneStatus?.phoneNumber || '',
    viewerIsCaptain: phoneStatus?.viewerIsCaptain === true,
    isLoading: isLoading || !hasCheckedPhone,
    onClose: handlePhoneModalClose,
    onSuccess: handlePhoneModalSuccess,
    refetch,
  };
};

export default usePhoneNumberStatus;
